import React, { useState, useEffect } from 'react';
import { signInWithEmailAndPassword, onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { auth, db } from '../config/firebase-config'; // ✅ CORRECT

function AdminLogin({ onLoginSuccess }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Check if user is already authenticated and is admin
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          console.log('Checking admin status for user:', user.uid);
          // Check if user has admin privileges
          const adminDoc = await getDoc(doc(db, 'admins', user.uid));
          console.log('Admin doc exists:', adminDoc.exists());
          
          if (adminDoc.exists()) {
            const adminData = adminDoc.data();
            console.log('Admin data:', adminData);
            
            if (adminData.isAdmin === true) {
              console.log('✅ User is admin, redirecting to dashboard');
              onLoginSuccess && onLoginSuccess();
            } else {
              console.log('❌ User is not admin');
            }
          } else {
            console.log('❌ Admin document does not exist');
          }
        } catch (error) {
          console.error('Error checking admin status:', error);
          // Add more specific error logging
          if (error.code === 'permission-denied') {
            console.error('Permission denied - check Firestore rules');
          }
        }
      }
    });

    return () => unsubscribe();
  }, [onLoginSuccess]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      console.log('Attempting admin login for:', email);
      
      // Sign in with Firebase Auth
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      console.log('User signed in:', user.uid);

      // Add a small delay to ensure auth state is updated
      await new Promise(resolve => setTimeout(resolve, 500));

      // Verify admin privileges
      const adminDoc = await getDoc(doc(db, 'admins', user.uid));
      
      if (!adminDoc.exists()) {
        console.error('Admin document not found for user:', user.uid);
        setError('Access denied: Admin account not found');
        await auth.signOut();
        return;
      }

      const adminData = adminDoc.data();
      console.log('Retrieved admin data:', adminData);
      
      if (!adminData.isAdmin) {
        console.error('User is not admin:', adminData);
        setError('Access denied: Insufficient privileges');
        await auth.signOut();
        return;
      }

      // Check if account is active
      if (adminData.status !== 'active') {
        console.error('Admin account is not active:', adminData.status);
        setError('Access denied: Account is suspended');
        await auth.signOut();
        return;
      }

      // Update last login timestamp
      try {
        await updateDoc(doc(db, 'admins', user.uid), {
          lastLogin: new Date(),
          lastLoginIP: await getUserIP()
        });
        console.log('Updated login timestamp');
      } catch (updateError) {
        console.warn('Could not update login timestamp:', updateError);
      }

      console.log('✅ Admin login successful:', user.email);
      onLoginSuccess && onLoginSuccess();

    } catch (error) {
      console.error('❌ Admin login failed:', error);
      
      switch (error.code) {
        case 'auth/user-not-found':
          setError('Admin account not found');
          break;
        case 'auth/wrong-password':
          setError('Invalid password');
          break;
        case 'auth/invalid-email':
          setError('Invalid email format');
          break;
        case 'auth/too-many-requests':
          setError('Too many failed attempts. Please try again later.');
          break;
        case 'auth/network-request-failed':
          setError('Network error. Please check your connection.');
          break;
        case 'permission-denied':
          setError('Database permission error. Please check Firestore rules.');
          break;
        default:
          setError(`Login failed: ${error.message}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function to get user IP (for security logging)
  const getUserIP = async () => {
    try {
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      return data.ip;
    } catch (error) {
      return 'Unknown';
    }
  };

  const containerStyle = {
    minHeight: '100vh',
    backgroundColor: '#111827',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'system-ui, -apple-system, sans-serif'
  };

  const formContainerStyle = {
    backgroundColor: '#1F2937',
    border: '8px solid #7C3AED',
    borderRadius: '24px',
    padding: '48px',
    width: '100%',
    maxWidth: '450px',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
  };

  const inputStyle = {
    width: '100%',
    padding: '16px',
    backgroundColor: '#374151',
    border: '2px solid #6B7280',
    borderRadius: '12px',
    color: 'white',
    fontSize: '16px',
    boxSizing: 'border-box',
    transition: 'border-color 0.3s ease'
  };

  const buttonStyle = {
    width: '100%',
    padding: '16px',
    backgroundColor: isLoading ? '#6B7280' : '#7C3AED',
    border: `3px solid ${isLoading ? '#9CA3AF' : '#6D28D9'}`,
    borderRadius: '12px',
    color: 'white',
    fontSize: '18px',
    fontWeight: 'bold',
    cursor: isLoading ? 'not-allowed' : 'pointer',
    transition: 'all 0.3s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px'
  };

  return (
    <div style={containerStyle}>
      <div style={formContainerStyle}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <div style={{ 
            fontSize: '64px', 
            marginBottom: '16px',
            background: 'linear-gradient(135deg, #7C3AED 0%, #EC4899 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>
            🛡️
          </div>
          <h1 style={{
            color: 'white',
            fontSize: '32px',
            fontWeight: 'bold',
            margin: '0 0 8px 0'
          }}>
            Admin Access
          </h1>
          <p style={{
            color: '#C4B5FD',
            fontSize: '16px',
            margin: 0
          }}>
            RequestASong Platform Administration
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div style={{
            backgroundColor: '#7F1D1D',
            border: '2px solid #DC2626',
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '24px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '20px' }}>⚠️</span>
              <p style={{
                color: '#FCA5A5',
                fontSize: '14px',
                margin: 0,
                fontWeight: 'bold'
              }}>
                {error}
              </p>
            </div>
          </div>
        )}

        {/* Login Form */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {/* Email Input */}
          <div>
            <label style={{
              display: 'block',
              color: 'white',
              fontSize: '16px',
              fontWeight: 'bold',
              marginBottom: '8px'
            }}>
              Admin Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@requestasong.com"
              required
              disabled={isLoading}
              style={{
                ...inputStyle,
                opacity: isLoading ? 0.7 : 1
              }}
              onFocus={(e) => e.target.style.borderColor = '#7C3AED'}
              onBlur={(e) => e.target.style.borderColor = '#6B7280'}
            />
          </div>

          {/* Password Input */}
          <div>
            <label style={{
              display: 'block',
              color: 'white',
              fontSize: '16px',
              fontWeight: 'bold',
              marginBottom: '8px'
            }}>
              Password
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter admin password"
                required
                disabled={isLoading}
                style={{
                  ...inputStyle,
                  opacity: isLoading ? 0.7 : 1,
                  paddingRight: '56px'
                }}
                onFocus={(e) => e.target.style.borderColor = '#7C3AED'}
                onBlur={(e) => e.target.style.borderColor = '#6B7280'}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && email && password && !isLoading) {
                    handleLogin(e);
                  }
                }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                disabled={isLoading}
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  color: '#9CA3AF',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  fontSize: '20px',
                  padding: '4px'
                }}
              >
                {showPassword ? '🙈' : '👁️'}
              </button>
            </div>
          </div>

          {/* Login Button */}
          <button
            type="button"
            onClick={handleLogin}
            disabled={isLoading || !email || !password}
            style={buttonStyle}
            onMouseOver={(e) => {
              if (!isLoading && email && password) {
                e.target.style.backgroundColor = '#6D28D9';
                e.target.style.transform = 'scale(1.02)';
              }
            }}
            onMouseOut={(e) => {
              if (!isLoading) {
                e.target.style.backgroundColor = '#7C3AED';
                e.target.style.transform = 'scale(1)';
              }
            }}
          >
            {isLoading ? (
              <>
                <div style={{
                  width: '20px',
                  height: '20px',
                  border: '2px solid white',
                  borderTop: '2px solid transparent',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}></div>
                Authenticating...
              </>
            ) : (
              <>
                🔐 Access Admin Dashboard
              </>
            )}
          </button>
        </div>

        {/* Security Notice */}
        <div style={{
          marginTop: '32px',
          backgroundColor: '#1E3A8A',
          border: '2px solid #3B82F6',
          borderRadius: '12px',
          padding: '16px'
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
            <span style={{ fontSize: '20px', flexShrink: 0 }}>🔒</span>
            <div>
              <h4 style={{
                color: '#BFDBFE',
                fontSize: '14px',
                fontWeight: 'bold',
                margin: '0 0 8px 0'
              }}>
                Security Notice
              </h4>
              <ul style={{
                color: '#BFDBFE',
                fontSize: '12px',
                lineHeight: '1.4',
                margin: 0,
                paddingLeft: '16px'
              }}>
                <li>All admin access is logged and monitored</li>
                <li>Failed login attempts are tracked by IP</li>
                <li>Two-factor authentication recommended</li>
                <li>Use strong, unique passwords only</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Debug info for development */}
        <div style={{
          marginTop: '24px',
          backgroundColor: '#065F46',
          border: '2px solid #10B981',
          borderRadius: '12px',
          padding: '16px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '16px' }}>🔍</span>
            <div>
              <p style={{
                color: '#A7F3D0',
                fontSize: '12px',
                margin: 0,
                fontWeight: 'bold'
              }}>
                DEBUG: Check browser console for detailed error logs
              </p>
              <p style={{
                color: '#A7F3D0',
                fontSize: '11px',
                margin: '4px 0 0 0'
              }}>
                Your admin document ID: 2VhzgBnKzLT2ZbkkqPk1AaiAdWh2
              </p>
            </div>
          </div>
        </div>
      </div>

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
}

export default AdminLogin;